import React, { useMemo, useState } from "react";
import { Popover } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import styles from "./CompactDateRangeField.module.css";

export interface CompactDateRangeFieldProps {
    label: string;
    start: Date | null;
    end: Date | null;
    onStartChange: (value: Date | null) => void;
    onEndChange: (value: Date | null) => void;
    className?: string;
}

const CompactDateRangeField: React.FC<CompactDateRangeFieldProps> = ({
    label,
    start,
    end,
    onStartChange,
    onEndChange,
    className = "",
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const display = useMemo(() => {
        const startLabel = t("dateStartShort") || "Start";
        const endLabel = t("dateEndShort") || "End";
        const startText = start ? format(start, "dd/MM/yy") : startLabel;
        const endText = end ? format(end, "dd/MM/yy") : endLabel;
        return `${startText} – ${endText}`;
    }, [start, end, t]);

    const hasValue = Boolean(start || end);

    return (
        <>
            <button
                type="button"
                className={`${styles.trigger} ${hasValue ? styles.triggerFilled : ""} ${className}`.trim()}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className={styles.label}>{label}</span>
                <span className={styles.value}>{display}</span>
                <svg className={`ic ${styles.calIcon}`} viewBox="0 0 24 24" aria-hidden>
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            </button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: { className: styles.popoverPaper },
                }}
            >
                <div className={styles.popover}>
                    <div className={styles.calendarCol}>
                        <div className={styles.calendarTitle}>{t("dateStartShort") || "Start"}</div>
                        <DateCalendar
                            value={start}
                            onChange={(value) => {
                                onStartChange(value);
                                if (value && end && value > end) {
                                    onEndChange(value);
                                }
                            }}
                            maxDate={end ?? undefined}
                        />
                    </div>
                    <div className={styles.calendarCol}>
                        <div className={styles.calendarTitle}>{t("dateEndShort") || "End"}</div>
                        <DateCalendar
                            value={end}
                            onChange={(value) => onEndChange(value)}
                            minDate={start ?? undefined}
                        />
                    </div>
                </div>
                <div className={styles.popoverFoot}>
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={() => {
                            onStartChange(null);
                            onEndChange(null);
                        }}
                    >
                        {t("resetFilters") || "Reset"}
                    </button>
                    <button type="button" className={styles.doneBtn} onClick={() => setAnchorEl(null)}>
                        {t("apply") || "Apply"}
                    </button>
                </div>
            </Popover>
        </>
    );
};

export default CompactDateRangeField;
